import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import CircleStyle from "ol/style/Circle";
import Text from "ol/style/Text";

export const PRIMARY_COLOR = "#00ffff";
export const POI_COLOR = "#d32f2f";

// TODO: need to determine appropriate color for each group
export const DEFAULT_REFERENCE_ALPHA = "66"; // ~40% opacity
// Question-layer shapes inherited from an earlier ESR, not made here
export const INHERITED_ALPHA = "66"; // ~40% opacity

export const colorMap: Record<string, string> = {
	project_limits: "#ffa500",
	state_row: "#f2689f",
	nat_species: "#62f860",
	Bat_Buffer: "#ec1010",
	High_Potential_Zones: "#ff7100",
	Hydric_NP: "#e89325",
	Hydric_Soils: "#835609",
	INAI: "#0c9f43",
	PLSS: "#9804f8",
	Roadside_Prairie_Inventory: "#0cbf0f",
	Wet_Poly: "#2eb4ef",
	Wetland: "#2954ff",
	Threatened_Endangered_Species_Buffers: "#0ecffa"
};

// Group each map question writes its shapes into, mirroring the backend
// defaults in esr.py — this is what decides the color they draw in
const questionGroup: Record<string, string> = {
	E92: "project_limits",
	E92D: "state_row",
};

// Color a question's shapes appear in on the map
export function getQuestionColor(questionId: string): string {
	if (questionId.startsWith("C82.") && questionId.endsWith(".MAP")) return "#c300ff"
	return colorMap[questionGroup[questionId]] ?? colorMap.project_limits;
}

// Palette cycled through for any group not in colorMap above (e.g. C82M's
// per-row "C82.<row>" groups) — picked by hashing the group string, so a
// given group always lands on the same color.
const DYNAMIC_GROUP_PALETTE = [
	"#2f28bd", "#28bd86", "#b128bd", "#9ebd28", "#2873bd",
	"#bd2898", "#bdbd28", "#4228bd", "#28bd99", "#bd28b7",
	"#8bbd28", "#28bd7a", "#a528bd", "#aabd28", "#5528bd",
	"#bd28a3", "#bdb228", "#3628bd", "#28bd8d", "#b828bd",
];

function hashString(str: string): number {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = (hash * 31 + str.charCodeAt(i)) | 0;
	}
	return Math.abs(hash);
}

export function getGroupColor(group: string | undefined): string | undefined {
	if (!group) return undefined;
	return colorMap[group] ?? DYNAMIC_GROUP_PALETTE[hashString(group) % DYNAMIC_GROUP_PALETTE.length];
}

// Color the reference layer draws in. A question's own previous version is cyan
// to set it apart from the current one; a different question keeps its own
// color, muted to sit behind what is being edited.
export function getReferenceColor(
	questionId: string,
	referenceQuestionId: string,
): string {
	return questionId === referenceQuestionId
		? PRIMARY_COLOR + DEFAULT_REFERENCE_ALPHA
		: getQuestionColor(referenceQuestionId) + DEFAULT_REFERENCE_ALPHA;
}

export function toStyle(fill: string, stroke: string) {
	return new Style({
		fill: new Fill({
			color: fill,
		}),
		stroke: new Stroke({
			color: stroke,
			width: 2,
		}),
	});
}

// A point of interest: a dot with its label always drawn above it
export function toPOIStyle(label: string, hovered = false) {
	return new Style({
		image: new CircleStyle({
			radius: hovered ? 10 : 6,
			fill: new Fill({ color: hovered ? "#1100ff": POI_COLOR }),
			stroke: new Stroke({ color: "white", width: hovered ? 3 : 2 }),
		}),
		text: new Text({
			text: label,
			offsetY: -16,
			font: "bold 13px Roboto, sans-serif",
			fill: new Fill({ color: "#222" }),
			stroke: new Stroke({ color: "white", width: 3 }),
		}),
	});
}